import { createRootRouteWithContext, Outlet, useLocation } from "@tanstack/react-router";
import posthog from "posthog-js";
import type { MutableRefObject } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { PostHogErrorBoundary } from "@/components/PostHogErrorBoundary";
import { SidebarTopBarComponent } from "@/components/Sidebar";
import AppSidebar from "@/components/Sidebar/Sidebar";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { applicationConfig } from "@/config";
import { ChatHistoryProvider } from "@/features/ask/context/ChatHistoryContext";
import type { useAuth } from "@/features/login/context/AuthContext";
import { DocumentProvider } from "@/features/nbi/context/DocumentContext";
import { DocumentCountProvider } from "@/features/nbi/context/DocumentCountContext";
import { NbiFormProvider } from "@/features/nbi/context/NbiFormContext";
import { NbiNavigationProvider } from "@/features/nbi/context/NbiNavigationContext";
import { SchemaEditProvider } from "@/features/schema-management/context/SchemaEditContext";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";

function PageviewTracker() {
    const location = useLocation();
    useEffect(() => {
        if (import.meta.env.VITE_POSTHOG_CAPTURE_PAGEVIEW === "true") {
            posthog.capture("$pageview", { $current_url: location.href });
        }
    }, [location.href]);
    return null;
}

const NbiFormSubmitContext = createContext<MutableRefObject<(() => void) | null> | null>(null);

export const useNbiFormSubmitRef = () => {
    return useContext(NbiFormSubmitContext);
};

const steps = [
    { name: "Product" },
    { name: "Goal" },
    { name: "Consent" },
    { name: "Upload Documents" },
    { name: "Loan Application Details" },
    { name: "Applicant Details" },
    { name: "Employment" },
    { name: "Income" },
    { name: "Expenses" },
    { name: "Assets" },
    { name: "Liabilities" },
    { name: "Review" },
    { name: "Outcome" },
];

function TopBarWithBreadcrumbs({ onToggleSidebar }: { onToggleSidebar: () => void }) {
    const breadcrumbs = useBreadcrumbs();
    return <SidebarTopBarComponent breadcrumbs={breadcrumbs} onToggleSidebar={onToggleSidebar} />;
}

interface RouterContext {
    auth: ReturnType<typeof useAuth> | undefined;
}

function RootLayout() {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const location = useLocation();
    const formSubmitRef = useRef<(() => void) | null>(null);

    const isLoginRoute =
        location.pathname === "/" ||
        location.pathname === `/${applicationConfig.countryCode}` ||
        location.pathname === `/${applicationConfig.countryCode}/`;
    const isNbiRoute = location.pathname.includes("/agent/application");
    const isPolicyRoute = location.pathname.includes("/agent/policy");
    const isSchemaEditRoute = location.pathname.includes("/agent/document");

    const handleToggleSidebar = () => {
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
            setIsMobileOpen(!isMobileOpen);
        } else {
            setIsCollapsed(!isCollapsed);
        }
    };

    const handleFormSubmit = () => {
        if (formSubmitRef.current) {
            formSubmitRef.current();
        }
    };

    if (isLoginRoute) {
        return (
            <PostHogErrorBoundary>
                <PageviewTracker />
                <Outlet />
            </PostHogErrorBoundary>
        );
    }

    const content = (
        <ChatHistoryProvider enabled={isPolicyRoute}>
            <SidebarProvider defaultOpen={true}>
                <div className="flex h-screen w-full bg-black overflow-hidden">
                    <AppSidebar isCollapsed={isCollapsed} isMobileOpen={isMobileOpen} />
                    <SidebarInset className="flex-1 flex flex-col overflow-hidden bg-black">
                        <TopBarWithBreadcrumbs onToggleSidebar={handleToggleSidebar} />
                        <NbiFormSubmitContext.Provider value={formSubmitRef}>
                            <PostHogErrorBoundary>
                                <Outlet />
                            </PostHogErrorBoundary>
                        </NbiFormSubmitContext.Provider>
                    </SidebarInset>
                </div>
            </SidebarProvider>
        </ChatHistoryProvider>
    );

    if (isSchemaEditRoute) {
        return (
            <>
                <PageviewTracker />
                <SchemaEditProvider>{content}</SchemaEditProvider>
            </>
        );
    }

    if (isNbiRoute) {
        return (
            <>
                <PageviewTracker />
                <NbiFormProvider>
                    <DocumentCountProvider>
                        <NbiNavigationProvider
                            totalSteps={steps.length}
                            stepNames={steps.map((step) => step.name)}
                            onSubmit={handleFormSubmit}
                        >
                            <DocumentProvider>{content}</DocumentProvider>
                        </NbiNavigationProvider>
                    </DocumentCountProvider>
                </NbiFormProvider>
            </>
        );
    }

    return (
        <>
            <PageviewTracker />
            {content}
        </>
    );
}

export const Route = createRootRouteWithContext<RouterContext>()({
    component: RootLayout,
});
